import { Address } from "@unleashed/models/Address";
import parseISO from "date-fns/parseISO";
import { default as f } from "date-fns/format";
import { t } from "i18next";
import { format as formatDate } from "date-fns";
import { SubscriptionStatus } from "@unleashed/enums/SubscriptionStatus";
import { BillingInterval } from "../models/BillingInterval";
import { BillingProvider } from "../models/BillingProvider";
import startOfToday from "date-fns/startOfToday";
import differenceInCalendarMonths from "date-fns/differenceInCalendarMonths";
import addDays from "date-fns/addDays";
import { Membership } from "../features/accounts/features/purchasedItems/features/memberships/models/Membership";
import { Subscription } from "../features/accounts/models/Subscription";
import { enUS } from "date-fns/locale";

export const getDisplayPhoneNumber = (value?: string): string => {
  if (!value) {
    return "";
  }
  if (value.length === 10) {
    return `(${value.substring(0, 3)}) ${value.substring(3, 6)}-${value.substring(6, 10)}`;
  }
  if (value.length === 11 && value.startsWith("1")) {
    return `+1 (${value.substring(1, 4)}) ${value.substring(4, 7)}-${value.substring(7, 11)}`;
  }

  return value ?? "";
};

export const getDisplayAddress = (value: Address, includeStreet = true): string => {
  let address = "";
  if (value.streetAddress && includeStreet) {
    address += value.streetAddress;
  }
  if (value.city) {
    if (value.streetAddress && includeStreet) {
      address += ", ";
    }
    address += value.city;
  }
  if (value.state) {
    if ((value.streetAddress && includeStreet) || value.city) {
      address += ", ";
    }
    address += value.state;
  }
  if (value.zipCode) {
    if (address.length) {
      address += " ";
    }
    address += value.zipCode;
  }

  return address;
};


export const getDisplayName = (value?: {
  firstName?: string;
  lastName?: string;
}): string => {
  if (!value) {
    return "";
  }
  const lastName = value.lastName?.trim() === "-" ? undefined : value.lastName;
  if (!value.firstName) {
    return lastName ?? "";
  }
  if (lastName) {
    return `${value.firstName} ${lastName}`;
  }

  return value.firstName;
};

export const getDisplayDate = (
  value?: string,
  format = "M/d/yyyy",
): string | undefined => {
  if (!value) {
    return undefined;
  }

  try {
    const date = parseISO(value);
    return f(date, format);
  }
  catch {
    return t`Invalid Date`;
  }
};

export const getDisplayDateTime = (
  value?: string,
  format = "M/d/yyyy h:mm a",
): string | undefined => {
  return getDisplayDate(value, format);
};

function convertIntlTimeToStdTime(twentyFourHourTime: number): string {
  let hours = twentyFourHourTime % 12;
  if (hours === 0) {
    hours = 12; // Handle midnight (00:00)
  }

  const minutes = Math.floor(twentyFourHourTime / 60) % 60;
  const ampm = twentyFourHourTime >= 12 ? "PM" : "AM";
  const paddedHours = String(hours).padStart(1, "0");
  const paddedMinutes = String(minutes).padStart(2, "0");

  return `${paddedHours}:${paddedMinutes} ${ampm}`;
}

export const getEventDateRange = (
  eventStartDate?: string,
  eventEndDate?: string,
): string | undefined => {
  if (!eventStartDate) {
    return undefined;
  }
  try {
    const fromDate = getDisplayDate(eventStartDate);
    const toDate = getDisplayDate(eventEndDate);
    if (fromDate === toDate || !toDate) {return fromDate;}
    return `${fromDate}-${toDate}`;
  }
  catch (error) {
    return t`Invalid Date Range`;
  }
};

export const getEventTimeSlot = (
  eventStartTime?: number,
  eventEndTime?: number,
): string => {
  if (!eventStartTime || !eventEndTime) {
    return "";
  }

  try {
    return `${convertIntlTimeToStdTime(eventStartTime)}-${convertIntlTimeToStdTime(eventEndTime)}`;
  }
  catch (error) {
    return t`Invalid Time Slot`;
  }
};

function splitStringByFirstOccurrence(str: string, char: string): [string, string | undefined] {
  const index = str.indexOf(char);

  if (index === -1) {
    return [str, undefined]; // Character not found, return the original string and undefined
  }
  else {
    const firstPart = str.substring(0, index);
    const secondPart = str.substring(index + 1);

    return [firstPart, secondPart];
  }
}

export const getDisplayTimeSlot = (
  from?: string,
  to?: string,
): string => {
  if (!from || !to) {
    return "";
  }

  try {
    const a1 = splitStringByFirstOccurrence(from, " ");
    const d1 = a1[0];
    const t1 = a1[1];
    const t2 = splitStringByFirstOccurrence(to, " ")[1];
    const fromDate = getDisplayDate(d1);

    return `${fromDate} ${t1}-${t2}`;
  }
  catch (error) {
    return t`Invalid Time Slot`;
  }
};

export const getDisplayTimeZonedDateTime = (
  value?: string,
  format = "M/d/yyyy - h:mm a zzz",
): string => {
  if (!value) return "";

  const date = new Date(value);
  const options: Intl.DateTimeFormatOptions = {
    timeZoneName: "short"
  };
  const timeZoneName = new Intl.DateTimeFormat("en-US", options)
    .formatToParts(date)
    .find(part => part.type === "timeZoneName")?.value;

  if (!timeZoneName) return formatDate(date, format.replace("zzz", ""), { locale: enUS });

  return formatDate(date, format.replace("zzz", `'${timeZoneName}'`), { locale: enUS });
};

export const getDisplayAge = (
  ageInMonths?: number,
  maxMonths = 36,
): string => {
  if (!ageInMonths) {
    return "";
  }

  // Display as months under 3 years
  return Math.floor(ageInMonths) <= (maxMonths - 1)
    ? t("{{count}} months", {
      count: Math.max(0, Math.ceil(ageInMonths))
    })
    : t("{{count}} years", {
      count: Math.floor(ageInMonths / 12)
    });
};

export const getDisplaySubscriptionStatus = (id?: number): string => {
  if (id === undefined || id === null) {
    return "";
  }

  switch (id) {
  case SubscriptionStatus.Active:
    return t`Active`;
  case SubscriptionStatus.Expired:
    return t`Expired`;
  case SubscriptionStatus.Inactive:
    return t`Inactive`;
  case SubscriptionStatus.Pending:
    return t`Pending`;
  case SubscriptionStatus.Used:
    return t`Used`;
  case SubscriptionStatus.Errored:
  case SubscriptionStatus.ProcessingFailed:
    return t`Errored`;
  case SubscriptionStatus.Suspended:
    return t`Paused`;
  case SubscriptionStatus.Upgraded:
  case SubscriptionStatus.UpgradePending:
  case SubscriptionStatus.UpgradeFailed:
  case SubscriptionStatus.Transferred:
  case SubscriptionStatus.Canceled:
  case SubscriptionStatus.CanceledPending:
    return t`Canceled`;
  case SubscriptionStatus.PastDue:
    return t`Past Due`;
  case SubscriptionStatus.Presale:
    return t`Presale`;
  default:
    return t`Unknown`;
  }
};

export const getDisplayBillingInterval = (
  billingInterval?: BillingInterval,
): string => {
  switch (billingInterval) {
  case "None":
    return t`Paid in Full`;
  case "Week":
  case "Months":
    return t`Recurring`;
  default:
    return t`Unknown`;
  }
};

export const getDisplayBillingProvider = (
  membership?: Membership | Subscription,
): BillingProvider => {
  if (membership?.recurringBilling ?? false) {
    return "JustiFi";
  }
  if (!membership?.externalReferenceId) {
    return t`Unknown`;
  }
  if (membership?.externalReferenceId.toLowerCase().startsWith("pma-")) {
    return "authorize.net";
  }
  if (membership?.externalReferenceId.length <= 8) {
    return "Braintree";
  }
  return "Recurly";
};

export const getDisplayContractDuration = (value?: {
  billingInterval?: BillingInterval,
  contractDuration?: number,
  productTypeId?: number,
  validToOffset?: number,
}): string => {
  if (value?.productTypeId === 1) {
    // Memberships use BillingInterval & ContractDuration
    const duration = value?.contractDuration ?? t`Unknown`;
    let interval;

    switch (value?.billingInterval) {
    case "None":
      interval = t`[N/A]`;
      break;
    case "Months":
      interval = t`Month`;
      break;
    case "Week":
      interval = t`Week`;
      break;
    default:
      interval = t`Unknown`;
    }

    if (interval) {
      return `${duration}-${interval}`;
    }
  }
  else if (value?.productTypeId === 7) {
    // PIF use ValidToOffset
    if (!value.validToOffset) {
      return t`Unknown`;
    }

    const start = startOfToday();
    const end = addDays(startOfToday(), value.validToOffset);
    const months = differenceInCalendarMonths(end, start);

    return t("{{count}}-Month", {count: months});
  }

  return "";
};

export const getDisplayBillingFrequency = (installmentCountPerCycle?: number): string => {
  if (installmentCountPerCycle === undefined || installmentCountPerCycle === null) {
    return t`Unknown`;
  }

  switch (installmentCountPerCycle) {
  case 1:
    return t`Monthly`;
  case 2:
    return t`Bi-monthly`;
  default:
    return t`Unknown`;
  }
};

export const getDisplayPercentage = (value?: number): string => {
  return t("{{value, number}} %", {value: value ?? 0});
};

export const isSubscriptionStatus = (expectedStatus: SubscriptionStatus, actualStatus: number | undefined) =>
  actualStatus === expectedStatus;

export const getTotusGiftCardDisplayName = (paymentProvider?: string): string => {
  return paymentProvider === t`TotusGiftCard` ? t`Digital Gift Card` : "";
};
